import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Target, Users, Globe, Award, Zap, ArrowRight, Rocket, Code, Lock, CheckCircle } from 'lucide-react';

interface AboutProps {
  setCurrentPage: (page: string) => void;
}

const About: React.FC<AboutProps> = ({ setCurrentPage }) => {
  const stats = [
    { icon: Globe, value: '50+', label: 'Countries Served' },
    { icon: Shield, value: '99.9%', label: 'Security Uptime' },
    { icon: Award, value: '25+', label: 'Industry Awards' }
  ];

  const innovations = [
    {
      icon: Code,
      title: "AI-Powered Security",
      description: "Advanced machine learning algorithms for proactive threat detection and response"
    },
    {
      icon: Lock,
      title: "Zero-Trust Architecture",
      description: "Comprehensive security framework ensuring verified access at every point"
    },
    {
      icon: Shield,
      title: "Quantum-Ready Security",
      description: "Future-proof encryption standards protecting against emerging threats"
    }
  ];

  const values = [
    {
      icon: Target,
      title: "Excellence",
      description: "Delivering exceptional security solutions through innovation and expertise",
      principles: [
        "Continuous improvement",
        "Quality assurance",
        "Best practices",
        "Industry standards"
      ]
    },
    {
      icon: Users,
      title: "Client Focus",
      description: "Understanding and addressing unique security challenges of each client",
      principles: [
        "Personalized solutions",
        "Dedicated support",
        "Proactive service",
        "Long-term partnership"
      ]
    },
    {
      icon: Rocket,
      title: "Innovation",
      description: "Pioneering advanced security solutions for tomorrow's challenges",
      principles: [
        "Research & development",
        "Emerging technologies",
        "Creative solutions",
        "Forward thinking"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-block mb-6"
          >
            <Shield className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            <span className="text-gradient">Securing Tomorrow's</span>
            <br />
            <span className="text-gradient">Digital Future</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-muted max-w-3xl mx-auto leading-relaxed"
          >
            At AstronShield, we're pioneering the future of cybersecurity with innovative solutions 
            that protect organizations in an increasingly connected world.
          </motion.p>
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20"
        >
          {stats.map((stat, index) => (
            <div key={index} className="glass-panel rounded-xl p-8 text-center hover-card">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-primary to-accent rounded-xl">
                <stat.icon className="h-8 w-8 text-white" />
              </div>
              <div className="text-4xl font-bold text-gradient mb-2">{stat.value}</div>
              <div className="text-lg text-muted">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Innovation Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Innovation at Our Core</h2>
            <p className="text-lg text-muted">
              Pioneering advanced security solutions for the digital age
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {innovations.map((innovation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-primary to-accent rounded-xl">
                  <innovation.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4">{innovation.title}</h3>
                <p className="text-muted">{innovation.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Core Values */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Core Values</h2>
            <p className="text-lg text-muted">
              Principles that guide our mission to protect and secure
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <value.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold ml-4">{value.title}</h3>
                </div>
                <p className="text-muted mb-6">{value.description}</p>
                <ul className="space-y-3">
                  {value.principles.map((principle, pIndex) => (
                    <li key={pIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{principle}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <Zap className="h-12 w-12 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Security?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Partner with AstronShield to protect your organization with cutting-edge cybersecurity solutions
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;